import { AfterViewInit, Component, Input, OnInit, ViewChild } from "@angular/core";
import { FormBuilder, FormControl, FormGroup } from "@angular/forms";
import { MatSort } from "@angular/material/sort";
import { MatTableDataSource } from "@angular/material/table";
import { Router } from "@angular/router";
import { IonModal, ModalController } from "@ionic/angular";
import { OidcSecurityService } from "angular-auth-oidc-client";
import { OdataTermineRequest } from "../../../api/models/odata-termine-request";
import { OdataTermineResponse } from "../../../api/models/odata-termine-response";
import { TermineService } from "../../../api/services/termine.service";
import { TerminWorkflowProcess, UserRole } from "../../enums/global-enums";
import {
  getLowerCaseInputValue,
  getStringFilterConditionForDatatable,
  isAktArtTechnik,
} from "../../global-functions/global-functions";
import { AuthService } from "../../services/auth.service";
import { BackService } from "../../services/back.service";
import { TerminService } from "../../services/termin.service";
import { GutachtenUebergebenComponent } from "../gutachten-uebergeben/gutachten-uebergeben.component";
import { TerminBearbeitenComponent } from "../termin-bearbeiten/termin-bearbeiten.component";
import { TerminWeitersetzenKommentarComponent } from "../termin-weitersetzen-kommentar/termin-weitersetzen-kommentar.component";

enum TabsSelector {
  Alle = "Alle",
  VNUrgenz = TerminWorkflowProcess.VNUrgenz,
  UrgenzAN = TerminWorkflowProcess.UrgenzAN,
  RueckfrageAn = TerminWorkflowProcess.RueckfrageAn,
  RueckfrageVu = TerminWorkflowProcess.RueckfrageVu,
  QCDurchfuehrung = TerminWorkflowProcess.QCDurchfuehrung,
  Kundentermin = TerminWorkflowProcess.Kundentermin,
  BerichtUrgent = TerminWorkflowProcess.BerichtUrgent,
  AbloeseAng = TerminWorkflowProcess.AbloeseAng,
  BerichtVE = TerminWorkflowProcess.BerichtVE,
}

enum SubStatusFilterOptions {
  Alle = "alle",
  Offen = "kt_offen",
  Postweg = "kt_offen_geraet_auf_postweg",
  Faellig = "faellig",
}

interface TerminTable {
  data: any[];
  filteredData: any[];
  currentFilter: SubStatusFilterOptions;
  count: {};
  isLoaded: boolean;
}

@Component({
  selector: "app-termintabelle-terminliste",
  templateUrl: "./termintabelle-terminliste.component.html",
  styleUrls: ["./termintabelle-terminliste.component.scss"],
  standalone: false,
})
export class TermintabelleTerminlisteComponent implements OnInit, AfterViewInit {
  @Input() backPath;
  @Input() excludeColumns: string[];
  @Input() selectorOptionsOverride: string[];
  @Input() disableFromDateFilter: boolean;
  @Input() disableTerminartFilter: boolean;

  @ViewChild(MatSort, { static: true }) sort: MatSort;
  @ViewChild(IonModal) modal: IonModal;
  @ViewChild("fromDate") dpFromDate;

  selectedTermin: OdataTermineResponse;
  currentBearbeitenModal: HTMLIonModalElement;
  currentWeitersetzenModal: HTMLIonModalElement;

  filterFormGroupTermineListForDatatable: FormGroup;

  displayedColumnsAdmin: string[] = [
    "gutachten",
    "beginn",
    "terminart",
    "aktbezeichnung",
    "sb2",
    "versicherung",
    "vn",
    "plz",
    "ort",
    "aktart",
    "zusatzart",
    "bemerkung",
    "sb1",
    "status",
  ];
  displayedColumns: string[] = [
    "gutachten",
    "beginn",
    "terminart",
    "aktbezeichnung",
    "vn",
    "plz",
    "ort",
    "aktart",
    "zusatzart",
    "bemerkung",
    "status",
  ];
  dataSourceTermineMatTable: MatTableDataSource<any>;
  today = new Date();

  selector: FormControl<TabsSelector>;
  selectorOptions;
  filter: FormGroup;
  termineDictionary: { [key in TabsSelector]: TerminTable } = {} as {
    [key in TabsSelector]: TerminTable;
  };
  loadedTermineDictionary: { [key in TabsSelector]: TerminTable } = {} as {
    [key in TabsSelector]: TerminTable;
  };
  selectorOptionsBackoffice: Omit<typeof TabsSelector, "BerichtVE"> = {
    Alle: TabsSelector.Alle,
    VNUrgenz: TabsSelector.VNUrgenz,
    UrgenzAN: TabsSelector.UrgenzAN,
    RueckfrageAn: TabsSelector.RueckfrageAn,
    RueckfrageVu: TabsSelector.RueckfrageVu,
    QCDurchfuehrung: TabsSelector.QCDurchfuehrung,
    Kundentermin: TabsSelector.Kundentermin,
    BerichtUrgent: TabsSelector.BerichtUrgent,
    AbloeseAng: TabsSelector.AbloeseAng,
  };

  selectorOptionsInteressent: Omit<typeof TabsSelector, "QCDurchfuehrung" | "BerichtVE"> = {
    Alle: TabsSelector.Alle,
    VNUrgenz: TabsSelector.VNUrgenz,
    UrgenzAN: TabsSelector.UrgenzAN,
    RueckfrageAn: TabsSelector.RueckfrageAn,
    RueckfrageVu: TabsSelector.RueckfrageVu,
    Kundentermin: TabsSelector.Kundentermin,
    BerichtUrgent: TabsSelector.BerichtUrgent,
    AbloeseAng: TabsSelector.AbloeseAng,
  };

  constructor(
    private fb: FormBuilder,
    private backService: BackService,
    private router: Router,
    private modalController: ModalController,
    private termineServiceBackend: TermineService,
    private oidcSecurityService: OidcSecurityService,
    private terminService: TerminService,
    public authService: AuthService,
  ) {
    this.resetTermineDictionary();
    this.dataSourceTermineMatTable = new MatTableDataSource([]);
  }

  ngOnInit() {
    if (this.selectorOptionsOverride?.length) {
      this.selectorOptions = this.selectorOptionsOverride;
    } else {
      if (this.authService.hasCurrentUserAccessForRole(UserRole.Interessent)) {
        this.selectorOptions = this.selectorOptionsInteressent;
      }
      if (this.authService.hasCurrentUserAccessForRole(UserRole.Backoffice)) {
        this.selectorOptions = this.selectorOptionsBackoffice;
      }
    }

    this.selector = new FormControl(TabsSelector.Alle);
    const fromDateNumber = this.today.getMonth() - 1;
    const fromDateDate = new Date();
    fromDateDate.setMonth(fromDateNumber);

    this.filter = this.fb.group({
      fromDate: [fromDateDate],
    });

    this.loadTermineData(true);

    this.displayedColumnsAdmin = this.displayedColumnsAdmin.filter((column) => !this.excludeColumns?.includes(column));
    this.displayedColumns = this.displayedColumns.filter((column) => !this.excludeColumns?.includes(column));
    this.filterFormGroupTermineListForDatatable = this.fb.group({
      beginn: [""],
      aktbezeichnung: [""],
      sb2: [""],
      versicherung: [""],
      vn: [""],
      plz: [""],
      ort: [""],
      aktart: [""],
      zusatzart: [""],
      bemerkung: [""],
      sb1: [""],
      status: [""],
      terminart: [""],
    });

    this.filterFormGroupTermineListForDatatable.valueChanges.subscribe(() => {
      this.dataSourceTermineMatTable.data = this.getFilteredDatatableDataForSelector(
        this.loadedTermineDictionary[this.selector.value].data,
      );
      this.loadTermineData(false);
    });
  }

  loadTermine() {
    this.dataSourceTermineMatTable.data = this.termineDictionary[this.selector.value].filteredData || [];
    if (this.filterFormGroupTermineListForDatatable) {
      this.dataSourceTermineMatTable.data = this.getFilteredDatatableDataForSelector(
        this.termineDictionary[this.selector.value].filteredData,
      );
    }
  }

  ngAfterViewInit() {
    this.initSorting();
  }

  compare(a: any, b: any, isAsc: boolean): number {
    if (a == null && b == null) return 0;
    if (a == null) return isAsc ? -1 : 1;
    if (b == null) return isAsc ? 1 : -1;
    return (a < b ? -1 : a > b ? 1 : 0) * (isAsc ? 1 : -1);
  }

  getFilteredDatatableDataForSelector(termine: OdataTermineResponse[]) {
    const beginn = getLowerCaseInputValue(this.filterFormGroupTermineListForDatatable.get("beginn").value);

    const aktbezeichnung = getLowerCaseInputValue(
      this.filterFormGroupTermineListForDatatable.get("aktbezeichnung").value,
    );

    const sb2 = getLowerCaseInputValue(this.filterFormGroupTermineListForDatatable.get("sb2").value);

    const versicherung = getLowerCaseInputValue(this.filterFormGroupTermineListForDatatable.get("versicherung").value);

    const vn = getLowerCaseInputValue(this.filterFormGroupTermineListForDatatable.get("vn").value);

    const plz = getLowerCaseInputValue(this.filterFormGroupTermineListForDatatable.get("plz").value);

    const ort = getLowerCaseInputValue(this.filterFormGroupTermineListForDatatable.get("ort").value);

    const aktart = getLowerCaseInputValue(this.filterFormGroupTermineListForDatatable.get("aktart").value);

    const zusatzart = getLowerCaseInputValue(this.filterFormGroupTermineListForDatatable.get("zusatzart").value);

    const bemerkung = getLowerCaseInputValue(this.filterFormGroupTermineListForDatatable.get("bemerkung").value);

    const sb1 = getLowerCaseInputValue(this.filterFormGroupTermineListForDatatable.get("sb1").value);

    const status = getLowerCaseInputValue(this.filterFormGroupTermineListForDatatable.get("status").value);

    const terminart = getLowerCaseInputValue(this.filterFormGroupTermineListForDatatable.get("terminart").value);

    return termine.filter(
      (termin) =>
        ((new Date(termin.beginn).toLocaleDateString("de-DE", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        }) !== null &&
          beginn !== null &&
          new Date(termin.beginn)
            .toLocaleDateString("de-DE", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
            })
            .indexOf(beginn) !== -1) ||
          !beginn) &&
        getStringFilterConditionForDatatable(termin.aktbezeichnung, aktbezeichnung) &&
        getStringFilterConditionForDatatable(termin.sb2, sb2) &&
        getStringFilterConditionForDatatable(termin.versicherung, versicherung) &&
        getStringFilterConditionForDatatable(termin.vn, vn) &&
        getStringFilterConditionForDatatable(termin.plz, plz) &&
        getStringFilterConditionForDatatable(termin.ort, ort) &&
        getStringFilterConditionForDatatable(termin.aktart, aktart) &&
        getStringFilterConditionForDatatable(termin.zusatzart, zusatzart) &&
        getStringFilterConditionForDatatable(termin.bemerkung, bemerkung) &&
        getStringFilterConditionForDatatable(termin.sb1, sb1) &&
        getStringFilterConditionForDatatable(termin.status, status) &&
        getStringFilterConditionForDatatable(termin.terminart, terminart),
    );
  }

  getAktStatusClass(termin: OdataTermineResponse) {
    return `status_${termin.akt_sub_status} ${this.checkTerminHeute(termin.beginn)}`;
  }

  checkTerminHeute(datum) {
    const today = this.today;
    const nd = new Date(datum);
    if (
      nd.getFullYear() === today.getFullYear() &&
      nd.getMonth() === today.getMonth() &&
      nd.getDate() === today.getDate()
    ) {
      return "termin_heute";
    } else {
      return "";
    }
  }

  openTerminModal(selectedTermin) {
    this.selectedTermin = selectedTermin;
    this.modal.present();
  }

  closeTerminModal() {
    this.modal.dismiss(null, "cancel");
  }

  retrieveAktQueryParams(termin) {
    return { aktid: termin.akt_id, snr: termin.schadennummer };
  }

  openAktOfSelectedTermin(event) {
    const queryParamsAkt = this.retrieveAktQueryParams(this.selectedTermin);
    this.backService.backPath = this.backPath;
    const url = this.router.serializeUrl(this.router.createUrlTree(["/details"], { queryParams: queryParamsAkt }));
    if (event.ctrlKey || event.metaKey) {
      window.open(url, "_blank");
    } else {
      this.router.navigate(["/details"], { queryParams: queryParamsAkt });
      this.modal.dismiss(null, "cancel");
    }
  }

  async openTerminBearbeitenModal(isTechnik: boolean) {
    if (this.currentBearbeitenModal) {
      return;
    }
    this.currentBearbeitenModal = await this.modalController.create({
      component: TerminBearbeitenComponent,
      cssClass: ["bigger-modal", "resizeable-modal"],
      backdropDismiss: false,
      componentProps: {
        aktId: this.selectedTermin.akt_id,
        aktTitle: this.selectedTermin.aktbezeichnung,
        aktType: this.selectedTermin.aktart,
        aktStatus: this.selectedTermin.akt_status,
        isTechnik: isTechnik,
        terminWorkflowProcess: this.selectedTermin.terminart,
        terminId: this.selectedTermin.termin_id,
        terminDatum: new Date(this.selectedTermin?.beginn),
      },
    });

    await this.currentBearbeitenModal.present();
    const { data, role } = await this.currentBearbeitenModal.onWillDismiss();
    if (role === "confirm") {
      this.loadTermineData(true);
      this.modal.dismiss(null, "cancel");
    }
    this.currentBearbeitenModal = null;
  }

  async openTerminWeitersetzenModal() {
    if (this.currentWeitersetzenModal) {
      return;
    }
    this.currentWeitersetzenModal = await this.modalController.create({
      component: TerminWeitersetzenKommentarComponent,
      cssClass: [],
      backdropDismiss: false,
      componentProps: {
        aktId: this.selectedTermin.akt_id,
        aktTitle: this.selectedTermin.aktbezeichnung,
      },
    });

    await this.currentWeitersetzenModal.present();
    const { data, role } = await this.currentWeitersetzenModal.onWillDismiss();
    this.currentWeitersetzenModal = null;
  }

  async openGutachtenUebergebenModalOfSelectedTermin() {
    const newestGutachten = this.selectedTermin?.gutachten?.reduce((latest: any, current: any) =>
      current.ErstellDatum > latest.ErstellDatum ? current : latest,
    );
    const modal = await this.modalController.create({
      component: GutachtenUebergebenComponent,
      cssClass: "",
      backdropDismiss: false,
      componentProps: {
        aktId: this.selectedTermin.akt_id,
        aktTitle: this.selectedTermin.aktbezeichnung,
        gutachtenId: newestGutachten["Oid"],
        showGutachtenVorschau: true,
      },
    });
    await modal.present();
  }

  loadTermineData(fetchNewData: boolean) {
    this.resetTermineDictionary();
    Object.values(this.selectorOptions).forEach((selector: TabsSelector) => {
      if (selector == TabsSelector.Alle) return;
      if (fetchNewData) {
        this.termineServiceBackend
          .termineOdataCreate(this.getTermineRequestParams(selector))
          .subscribe((response: OdataTermineResponse[]) => {
            this.populateTermineDictionary(selector, response);
            this.loadedTermineDictionary = JSON.parse(JSON.stringify(this.termineDictionary));
          });
      } else {
        this.populateTermineDictionary(
          selector,
          this.getFilteredDatatableDataForSelector(this.loadedTermineDictionary[selector].data),
        );
      }
    });
  }

  populateTermineDictionary(selector: TabsSelector, termine: OdataTermineResponse[]) {
    this.termineDictionary[selector] = {
      data: termine,
      filteredData: termine,
      currentFilter: SubStatusFilterOptions.Alle,
      count: {
        [SubStatusFilterOptions.Alle.toString()]: termine.length,
        [SubStatusFilterOptions.Offen.toString()]: termine.filter(
          (entry) => entry.akt_sub_status === SubStatusFilterOptions.Offen,
        ).length,
        [SubStatusFilterOptions.Postweg.toString()]: termine.filter(
          (entry) => entry.akt_sub_status === SubStatusFilterOptions.Postweg,
        ).length,
        [SubStatusFilterOptions.Faellig.toString()]: termine.filter(
          (entry) => entry.akt_sub_status === SubStatusFilterOptions.Faellig,
        ).length,
      },
      isLoaded: true,
    };
    this.termineDictionary[TabsSelector.Alle].data.push(...this.termineDictionary[selector].data);
    this.termineDictionary[TabsSelector.Alle].count[SubStatusFilterOptions.Alle.toString()] +=
      this.termineDictionary[selector].count[SubStatusFilterOptions.Alle.toString()];
    this.termineDictionary[TabsSelector.Alle].count[SubStatusFilterOptions.Offen.toString()] +=
      this.termineDictionary[selector].count[SubStatusFilterOptions.Offen.toString()];
    this.termineDictionary[TabsSelector.Alle].count[SubStatusFilterOptions.Postweg.toString()] +=
      this.termineDictionary[selector].count[SubStatusFilterOptions.Postweg.toString()];
    this.termineDictionary[TabsSelector.Alle].count[SubStatusFilterOptions.Faellig.toString()] +=
      this.termineDictionary[selector].count[SubStatusFilterOptions.Faellig.toString()];
    this.termineDictionary[TabsSelector.Alle].isLoaded = Object.keys(this.termineDictionary).every((key) => {
      return (
        key === TabsSelector.Alle ||
        this.termineDictionary[key].isLoaded ||
        !Object.values(this.selectorOptions).includes(key)
      );
    });

    if (this.selector.value == TabsSelector.Alle) {
      this.updateFilterForSelectorOption(this.selector.value, this.termineDictionary[TabsSelector.Alle].currentFilter);

      this.termineDictionary[TabsSelector.Alle].filteredData = [
        ...this.termineDictionary[TabsSelector.Alle].filteredData,
      ];
    }

    this.loadTermine();
  }

  getTermineRequestParams(workflowTerminart: string) {
    const params = {
      token: this.oidcSecurityService.getToken(),
      workflow_terminart: workflowTerminart,
    } as OdataTermineRequest;
    if (!this.disableFromDateFilter) {
      params.von_datum = this.fromDate.toISOString();
    }
    return params;
  }

  setCurrentFilter(event, selectorOption, subStatusFilterOption) {
    this.setTabsSelector(event, selectorOption);
    this.updateFilterForSelectorOption(selectorOption, subStatusFilterOption);
    event.stopPropagation();
    event.stopImmediatePropagation();
  }

  setTabsSelector(event, selectorOption) {
    this.selector.setValue(selectorOption);
    this.updateFilterForSelectorOption(selectorOption, this.termineDictionary[selectorOption].currentFilter);
    event.stopPropagation();
    event.stopImmediatePropagation();
  }

  updateFilterForSelectorOption(selectorOption, subStatusFilterOption) {
    this.termineDictionary[selectorOption].currentFilter = subStatusFilterOption;
    if (subStatusFilterOption == SubStatusFilterOptions.Alle) {
      this.termineDictionary[selectorOption].filteredData = this.termineDictionary[selectorOption].data;
    } else {
      this.termineDictionary[selectorOption].filteredData = this.termineDictionary[selectorOption].data.filter(
        (entry) => entry.akt_sub_status === subStatusFilterOption,
      );
    }

    this.loadTermine();
  }

  resetTermineDictionary() {
    Object.values(TabsSelector).forEach((selector) => {
      this.termineDictionary[selector as TabsSelector] = {
        data: [],
        filteredData: [],
        currentFilter: SubStatusFilterOptions.Alle,
        count: {
          [SubStatusFilterOptions.Alle.toString()]: 0,
          [SubStatusFilterOptions.Offen.toString()]: 0,
          [SubStatusFilterOptions.Postweg.toString()]: 0,
          [SubStatusFilterOptions.Faellig.toString()]: 0,
        },
        isLoaded: false,
      };
    });
  }

  get fromDate(): Date {
    return this.filter.controls.fromDate.value;
  }

  set fromDate(date: Date) {
    this.filter.controls.fromDate.setValue(date);
  }

  fromDateChanged($event) {
    this.fromDate = new Date($event.target.value);
    this.loadTermineData(true);
  }

  getTerminartSortString(terminartString: string) {
    switch (terminartString) {
      case TabsSelector.VNUrgenz:
        return "01 " + terminartString;
      case TabsSelector.UrgenzAN:
        return "02 " + terminartString;
      case TabsSelector.RueckfrageVu:
        return "03 " + terminartString;
      case TabsSelector.RueckfrageAn:
        return "04 " + terminartString;
      case TabsSelector.QCDurchfuehrung:
        return "05 " + terminartString;
      case TabsSelector.Kundentermin:
        return "06 " + terminartString;
      case TabsSelector.BerichtUrgent:
        return "07 " + terminartString;
      case TabsSelector.AbloeseAng:
        return "08 " + terminartString;
      case TabsSelector.BerichtVE:
        return "09 " + terminartString;
    }
  }

  initSorting() {
    this.dataSourceTermineMatTable.sort = this.sort;

    this.dataSourceTermineMatTable.sortData = (data, sort) => {
      const active = sort.active;
      const direction = sort.direction;

      if (!active || direction === "") return data;

      const isAsc = direction === "asc";

      return data.slice().sort((a, b) => {
        let aPrimary = this.dataSourceTermineMatTable.sortingDataAccessor(a, active);
        let bPrimary = this.dataSourceTermineMatTable.sortingDataAccessor(b, active);

        if (active === "terminart") {
          aPrimary = this.getTerminartSortString(aPrimary as string);
          bPrimary = this.getTerminartSortString(bPrimary as string);
        }

        const primaryComparison = this.compare(aPrimary, bPrimary, isAsc);
        if (primaryComparison !== 0) return primaryComparison;

        const aBeginn = this.dataSourceTermineMatTable.sortingDataAccessor(a, "beginn");
        const bBeginn = this.dataSourceTermineMatTable.sortingDataAccessor(b, "beginn");

        return this.compare(aBeginn, bBeginn, true);
      });
    };
  }

  isTerminBearbeitenAllowed(selectedTermin: OdataTermineResponse): boolean {
    return (
      this.terminService.isKundenterminBestaetigenAllowed(
        selectedTermin?.aktart,
        selectedTermin?.akt_sub_status,
        selectedTermin?.terminart === TerminWorkflowProcess.Kundentermin,
        selectedTermin?.erledigt,
        selectedTermin?.aktsparte,
      ) ||
      selectedTermin?.terminart === TerminWorkflowProcess.RueckfrageAn ||
      selectedTermin?.terminart === TerminWorkflowProcess.BerichtUrgent
    );
  }

  isWeitersetzenAllowedForTermin(selectedTermin: OdataTermineResponse): boolean {
    return this.terminService.isKundenterminWeitersetzenAllowed(
      selectedTermin?.aktart,
      selectedTermin?.akt_sub_status,
      selectedTermin?.terminart === TerminWorkflowProcess.Kundentermin,
      selectedTermin?.erledigt,
      selectedTermin?.aktsparte,
    );
  }

  protected readonly UserRole = UserRole;
  protected readonly isAktArtTechnik = isAktArtTechnik;
  protected readonly TerminWorkflowProcess = TerminWorkflowProcess;
  protected readonly Object = Object;
  protected readonly SubStatusFilterOptions = SubStatusFilterOptions;
}
